import Head from "next/head";
import Image from "next/image";

import { BadgeCheck, Briefcase, Calendar, Check, Clock, Moon, Phone, Shield, Star, UserCheck, Zap } from "lucide-react";

import useCalApi from "@/components/hooks/useCalApi";
import { useCalendarLink } from "@/components/hooks/useCalendarLink";
import { UnderlineSVG } from "@/components/icons/HomePage";
import Footer from "@/components/marketing/pieces/Footer";
import NavbarLegacy from "@/components/marketing/pieces/NavbarLegacy";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { clashGrotesk, jakartaSans } from "@/styles/fonts";

export default function AiVoiceAssistantPage() {
  useCalApi();
  const { calPath } = useCalendarLink();

  return (
    <>
      <Head>
        <title>AI Voice Assistant for Home Service Businesses | Rebolt</title>
        <meta
          name="description"
          content="Never miss a call again. Rebolt's AI Voice Assistant answers calls, filters spam, and books jobs 24/7 for home service professionals."
        />
      </Head>

      <NavbarLegacy />

      <main className={cn("overflow-hidden pt-16 bg-clay-100 relative", jakartaSans.variable, clashGrotesk.variable)}>
        <div className="absolute inset-0 w-full h-screen pointer-events-none">
          <Image
            src="/HeroBG.png"
            alt="Hero Background"
            width={1920}
            height={1080}
            className="object-cover w-full opacity-60"
            priority
          />
        </div>

        <div className="flex flex-col items-center justify-center relative">
          {/* Hero Section */}
          <section className="min-h-[90vh] flex flex-col items-center justify-center gap-8 md:gap-10 px-4 pt-32 pb-32">
            <div className="flex flex-col gap-6 items-center justify-center max-w-5xl text-ash-900 text-center relative z-10">
              <h1 className="font-clash font-medium text-5xl md:text-7xl leading-[0.95] tracking-snug text-center relative max-w-4xl mx-auto">
                Missed calls ={" "}
                <span className="relative inline-block text-rebolt-500">
                  lost revenue
                  <UnderlineSVG className="absolute -bottom-2.5 md:-bottom-4 left-1/2 -translate-x-1/2 w-full text-ash-900" />
                </span>
              </h1>

              <p className="text-lg md:text-2xl text-ash-600 max-w-2xl mx-auto leading-relaxed mt-4">
                Rebolt&apos;s AI Voice Assistant answers every call, filters out spam, and captures leads while
                you&apos;re on the job or asleep.
              </p>

              <div className="relative w-full max-w-5xl mx-auto mt-8 ">
                <div className="bg-white rounded-2xl shadow-2xl shadow-ash-900/10 border border-ash-200 overflow-hidden">
                  <div className="relative aspect-21/9 w-full">
                    <Image
                      src="/ai-voice-assistant-annotated-v2.png"
                      alt="Graph showing peak missed calls at dinner time (6-7pm)"
                      fill
                      className="object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-4 w-full justify-center">
                <Button
                  size="lg"
                  variant="rebolt-secondary"
                  className="text-lg px-8 py-4 h-auto w-full sm:w-auto"
                  data-cal-link={calPath}
                  data-cal-config='{"layout":"month_view"}'
                >
                  Get a free demo
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-4 h-auto w-full sm:w-auto bg-transparent rounded-full"
                  onClick={() => {
                    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  See how it works
                </Button>
              </div>
            </div>
          </section>

          {/* Problem Section */}
          <section className="w-full pt-32 pb-20 px-4 bg-white relative">
            <div className="max-w-(--breakpoint-xl) mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                <div className="flex flex-col gap-4 p-6 rounded-2xl bg-ash-100/50 border border-ash-200">
                  <div className="w-12 h-12 rounded-xl bg-lava-100 flex items-center justify-center mb-2">
                    <Phone className="w-6 h-6 text-lava-600" />
                  </div>
                  <h3 className="font-clash text-2xl font-medium text-ash-900">Never Miss a Lead</h3>
                  <p className="text-ash-600 text-lg leading-relaxed">
                    <strong>Speed to lead is the name of the game.</strong> 70% of callers won&apos;t leave a
                    voicemail—they just move on to the next number on Google.
                  </p>
                </div>

                <div className="flex flex-col gap-4 p-6 rounded-2xl bg-ash-100/50 border border-ash-200">
                  <div className="w-12 h-12 rounded-xl bg-rebolt-100 flex items-center justify-center mb-2">
                    <Moon className="w-6 h-6 text-rebolt-600" />
                  </div>
                  <h3 className="font-clash text-2xl font-medium text-ash-900">24/7 Coverage</h3>
                  <p className="text-ash-600 text-lg leading-relaxed">
                    Capture leads evenings and weekends without being glued to your phone. Let the AI handle the
                    late-night emergency calls professionally.
                  </p>
                </div>
                <div className="flex flex-col gap-4 p-6 rounded-2xl bg-ash-100/50 border border-ash-200">
                  <div className="w-12 h-12 rounded-xl bg-honey-100 flex items-center justify-center mb-2">
                    <Shield className="w-6 h-6 text-honey-600" />
                  </div>
                  <h3 className="font-clash text-2xl font-medium text-ash-900">Block Spam Forever</h3>
                  <p className="text-ash-600 text-lg leading-relaxed">
                    Robocalls and salespeople waste your time. Our AI screens every call so your phone only rings for
                    real money-making opportunities.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section id="how-it-works" className="w-full py-24 px-4 bg-ash-900 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/3 h-full bg-linear-to-l from-ash-800 to-transparent pointer-events-none" />
            <div className="max-w-(--breakpoint-xl) mx-auto relative z-10">
              <div className="text-center mb-16">
                <p className="text-rebolt-500 font-semibold uppercase tracking-wider mb-4">Simple Setup</p>
                <h2 className="font-clash text-4xl md:text-5xl lg:text-6xl font-medium mb-6">How it works</h2>
                <p className="text-xl text-ash-300 max-w-2xl mx-auto">
                  No new hardware. No complex software. just a smart assistant that works 24/7.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
                {/* Connector Line (Desktop) */}
                <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-ash-700 border-t border-dashed border-ash-600 z-0"></div>

                <div className="flex flex-col items-center text-center gap-6 relative z-10">
                  <div className="w-24 h-24 rounded-full bg-ash-800 border-4 border-ash-700 flex items-center justify-center shadow-xl">
                    <span className="text-3xl font-clash font-bold text-white">1</span>
                  </div>
                  <h3 className="text-2xl font-medium text-white">Forward Calls</h3>
                  <p className="text-ash-300">
                    Set call forwarding to your dedicated AI number. You can forward all calls, or just the ones you
                    miss.
                  </p>
                </div>

                <div className="flex flex-col items-center text-center gap-6 relative z-10">
                  <div className="w-24 h-24 rounded-full bg-rebolt-600 border-4 border-rebolt-500 flex items-center justify-center shadow-xl shadow-rebolt-900/50">
                    <span className="text-3xl font-clash font-bold text-white">2</span>
                  </div>
                  <h3 className="text-2xl font-medium text-white">AI Answers</h3>
                  <p className="text-ash-300">
                    The AI answers instantly in a natural voice. It qualifies the customer, answers questions, and
                    blocks spam.
                  </p>
                </div>

                <div className="flex flex-col items-center text-center gap-6 relative z-10">
                  <div className="w-24 h-24 rounded-full bg-ash-800 border-4 border-ash-700 flex items-center justify-center shadow-xl">
                    <span className="text-3xl font-clash font-bold text-white">3</span>
                  </div>
                  <h3 className="text-2xl font-medium text-white">You Get Paid</h3>
                  <p className="text-ash-300">
                    Qualified leads are sent to you instantly via SMS or scheduled directly on your calendar.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Benefits & Features */}
          <section className="w-full py-24 px-4 bg-clay-100">
            <div className="max-w-(--breakpoint-xl) mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div className="flex flex-col gap-8 order-2 lg:order-1">
                  <div className="flex flex-col gap-4">
                    <p className="text-rebolt-600 font-semibold uppercase tracking-wider">Features</p>
                    <h2 className="font-clash text-4xl md:text-5xl font-medium text-ash-900 leading-tight">
                      More than just <br /> voicemail
                    </h2>
                    <p className="text-xl text-ash-600">
                      It&apos;s like having a full-time office manager who never sleeps, never takes a break, and costs
                      a fraction of the price.
                    </p>
                  </div>

                  <div className="flex flex-col gap-6">
                    {[
                      {
                        title: "Natural Conversation",
                        desc: "Sounds human, not robotic. Understands context and handles interruptions gracefully.",
                      },
                      {
                        title: "Smart Scheduling",
                        desc: "Connects to your calendar to book appointments while you work.",
                      },
                      {
                        title: "Lead Qualification",
                        desc: "Asks specific questions (e.g., 'What type of leak?', 'Is it an emergency?') before notifying you.",
                      },
                      {
                        title: "Seamless Handoff",
                        desc: "Intelligently transfers the call to you if the customer needs to speak to a human immediately.",
                      },
                    ].map((feature, idx) => (
                      <div key={idx} className="flex gap-4 items-start">
                        <div className="mt-1 w-6 h-6 rounded-full bg-rebolt-100 flex items-center justify-center shrink-0">
                          <Check className="w-4 h-4 text-rebolt-600" />
                        </div>
                        <div>
                          <h4 className="text-xl font-medium text-ash-900">{feature.title}</h4>
                          <p className="text-ash-600 mt-1">{feature.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4">
                    <Button
                      size="lg"
                      variant="rebolt-secondary"
                      data-cal-link={calPath}
                      data-cal-config='{"layout":"month_view"}'
                    >
                      See it in action
                    </Button>
                  </div>
                </div>

                <div className="order-1 lg:order-2 relative">
                  {/* Abstract Visual Representation of the App/Interface */}
                  <div className="bg-white rounded-3xl shadow-xl border border-ash-200 p-8 relative z-10 overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-linear-to-r from-rebolt-500 to-rebolt-400" />

                    {/* Mock Chat/Call Log */}
                    <div className="flex flex-col gap-6">
                      <div className="flex items-center justify-between border-b border-ash-100 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-rebolt-100 flex items-center justify-center">
                            <Phone className="w-5 h-5 text-rebolt-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-ash-900">Incoming Call</p>
                            <p className="text-sm text-ash-500">Processed by AI • 2 mins ago</p>
                          </div>
                        </div>
                        <BadgeCheck className="w-6 h-6 text-mint-500" />
                      </div>

                      <div className="flex flex-col gap-4 text-sm md:text-base">
                        <div className="bg-ash-100 p-4 rounded-r-2xl rounded-bl-2xl max-w-[90%] self-start">
                          <p className="text-ash-700">
                            <strong>Client:</strong> Hi, I need someone to look at my AC unit. It&apos;s making a weird
                            noise and not cooling.
                          </p>
                        </div>
                        <div className="bg-rebolt-100 p-4 rounded-l-2xl rounded-br-2xl max-w-[90%] self-end">
                          <p className="text-ash-900">
                            <strong>AI Assistant:</strong> I can help with that. To get the right technician out to you,
                            is this for a residential or commercial property?
                          </p>
                        </div>
                        <div className="bg-ash-100 p-4 rounded-r-2xl rounded-bl-2xl max-w-[90%] self-start">
                          <p className="text-ash-700">
                            <strong>Client:</strong> It&apos;s for my house. Can you come today?
                          </p>
                        </div>
                        <div className="bg-rebolt-100 p-4 rounded-l-2xl rounded-br-2xl max-w-[90%] self-end">
                          <p className="text-ash-900">
                            <strong>AI Assistant:</strong> I see we have an opening today at 4:00 PM. Would you like me
                            to book that for you?
                          </p>
                        </div>

                        <div className="mt-2 flex items-center gap-2 p-3 bg-mint-100 border border-mint-200 rounded-lg">
                          <Calendar className="w-5 h-5 text-mint-600" />
                          <span className="text-mint-800 font-medium">Appointment Scheduled: Today @ 4:00 PM</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Decorative Elements */}
                  <div className="absolute -top-10 -right-10 w-64 h-64 bg-rebolt-200 rounded-full mix-blend-multiply blur-3xl opacity-30" />
                  <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-rebolt-100 rounded-full mix-blend-multiply blur-3xl opacity-30" />
                </div>
              </div>
            </div>
          </section>

          {/* Built For Section */}
          <section className="w-full py-24 px-4 bg-white">
            <div className="max-w-(--breakpoint-xl) mx-auto text-center">
              <h2 className="font-clash text-3xl md:text-5xl font-medium text-ash-900 mb-16">
                Built specifically for <br />
                <span className="relative inline-block mt-2">
                  home service businesses
                  <UnderlineSVG className="absolute -bottom-2 md:-bottom-3 left-1/2 -translate-x-1/2 w-full text-rebolt-500" />
                </span>
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { icon: Zap, label: "Electricians" },
                  { icon: UserCheck, label: "Plumbers" },
                  { icon: Moon, label: "HVAC" },
                  { icon: Briefcase, label: "Contractors" },
                  { icon: Phone, label: "Roofers" },
                  { icon: Shield, label: "Landscapers" },
                  { icon: Star, label: "Cleaners" },
                  { icon: Clock, label: "Pest Control" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col items-center justify-center gap-4 p-8 rounded-2xl bg-ash-100/50 border border-ash-200 hover:border-rebolt-200 hover:shadow-lg transition-all group"
                  >
                    <item.icon className="w-8 h-8 text-ash-400 group-hover:text-rebolt-500 transition-colors" />
                    <span className="font-medium text-ash-800 text-lg">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Trust / CTA Section */}
          <section className="w-full py-24 px-4 bg-ash-900 text-white text-center relative overflow-hidden">
            {/* Background Pattern */}
            <div
              className="absolute inset-0 opacity-10"
              style={{ backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)", backgroundSize: "30px 30px" }}
            ></div>

            <div className="max-w-4xl mx-auto relative z-10 flex flex-col gap-8 items-center">
              <div className="flex items-center gap-2 px-4 py-1 rounded-full bg-white/10 text-white/90 text-sm backdrop-blur-xs border border-white/10">
                <span className="w-2 h-2 rounded-full bg-mint-500 animate-pulse"></span>
                Systems Operational
              </div>

              <h2 className="font-clash text-4xl md:text-6xl font-medium leading-tight">
                Ready to stop missing calls?
              </h2>
              <p className="text-xl md:text-2xl text-ash-300 max-w-2xl">
                Join 1,000+ service pros who trust Rebolt to handle their communications. Set up takes minutes.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center pt-8">
                <Button
                  size="lg"
                  variant="rebolt-secondary"
                  className="text-lg px-10 py-7 h-auto w-full sm:w-auto text-white"
                  data-cal-link={calPath}
                  data-cal-config='{"layout":"month_view"}'
                >
                  Start your free trial
                </Button>
              </div>
              <p className="text-ash-400 text-sm mt-4">No credit card required for demo call.</p>
            </div>
          </section>

          <Footer />
        </div>
      </main>
    </>
  );
}
